/**
 * DocumentCategories Component
 *
 * Left sidebar showing document categories with counts for filtering.
 * Based on CASEFILE_FUNCTIONAL_SPEC.md Section 2.
 *
 * Categories match backend UI_CATEGORY_MAP values:
 * - "Medical Records", "Insurance/EOBs", "IEP/504 Plans",
 * - "Legal Documents", "School Records", "Therapy Notes", "Other"
 */

import {
  FolderOpen,
  FileText,
  Shield,
  GraduationCap,
  Scale,
  BookOpen,
  Heart,
  MoreHorizontal,
} from 'lucide-react';

// Category values match backend UI_CATEGORY_MAP output
export type DocumentCategory =
  | 'all'
  | 'Medical Records'
  | 'Insurance/EOBs'
  | 'IEP/504 Plans'
  | 'Legal Documents'
  | 'School Records'
  | 'Therapy Notes'
  | 'Other';

export interface CategoryCount {
  category: DocumentCategory;
  label: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
}

interface DocumentCategoriesProps {
  counts: CategoryCount[];
  activeCategory: DocumentCategory;
  onCategoryChange: (category: DocumentCategory) => void;
  className?: string;
}

// Default categories structure - matches backend UI_CATEGORY_MAP
export const defaultCategories: CategoryCount[] = [
  {
    category: 'all',
    label: 'All Documents',
    count: 0,
    icon: FolderOpen,
  },
  {
    category: 'Medical Records',
    label: 'Medical Records',
    count: 0,
    icon: FileText,
  },
  {
    category: 'Insurance/EOBs',
    label: 'Insurance/EOBs',
    count: 0,
    icon: Shield,
  },
  {
    category: 'IEP/504 Plans',
    label: 'IEP/504 Plans',
    count: 0,
    icon: GraduationCap,
  },
  {
    category: 'Legal Documents',
    label: 'Legal Documents',
    count: 0,
    icon: Scale,
  },
  {
    category: 'School Records',
    label: 'School Records',
    count: 0,
    icon: BookOpen,
  },
  {
    category: 'Therapy Notes',
    label: 'Therapy Notes',
    count: 0,
    icon: Heart,
  },
  {
    category: 'Other',
    label: 'Other',
    count: 0,
    icon: MoreHorizontal,
  },
];

export function DocumentCategories({
  counts,
  activeCategory,
  onCategoryChange,
  className = '',
}: DocumentCategoriesProps): JSX.Element {
  // Merge default categories with actual counts
  const categories = defaultCategories.map((defaultCat) => {
    const countData = counts.find((c) => c.category === defaultCat.category);
    return {
      ...defaultCat,
      count: countData?.count ?? 0,
    };
  });

  return (
    <div className={`bg-white border-r border-soft-neutral h-full ${className}`}>
      <div className="p-4">
        <h2 className="font-semibold text-ink text-sm uppercase tracking-wider mb-4">
          Categories
        </h2>

        <nav className="space-y-1">
          {categories.map((category) => {
            const Icon = category.icon;
            const isActive = activeCategory === category.category;
            const isDisabled = category.count === 0 && category.category !== 'all';

            return (
              <button
                key={category.category}
                onClick={() => {
                  if (!isDisabled) {
                    onCategoryChange(category.category);
                  }
                }}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left
                  transition-all duration-250 group
                  ${isActive
                    ? 'bg-evergreen/10 text-evergreen'
                    : isDisabled
                      ? 'text-muted cursor-default'
                      : 'text-slate-ink hover:bg-soft-neutral hover:text-ink'
                  }
                `}
                disabled={isDisabled}
              >
                {/* Icon */}
                <Icon className={`w-5 h-5 flex-shrink-0 ${
                  isActive ? 'text-evergreen' : isDisabled ? 'text-muted' : ''
                }`} />

                {/* Label */}
                <span className="flex-1 text-sm font-medium truncate">
                  {category.label}
                </span>

                {/* Count badge */}
                <span className={`
                  text-xs px-2 py-0.5 rounded-full
                  ${isActive
                    ? 'bg-evergreen/20 text-evergreen'
                    : isDisabled
                      ? 'bg-soft-neutral text-muted'
                      : 'bg-soft-neutral text-slate-ink'
                  }
                `}>
                  {category.count}
                </span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

export default DocumentCategories;
